/* eslint-disable react/sort-comp */
import { Box, Button, Card, CardContent, Typography } from '@mui/material'
import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  readonly children: ReactNode
  readonly fallback?: ReactNode
}

interface State {
  readonly hasError: boolean
  readonly error: Error | null
  readonly errorInfo: ErrorInfo | null
}

// Functional component for the error UI (defined first to satisfy no-use-before-define)
interface ErrorFallbackProps {
  readonly error: Error | null
  readonly errorInfo: ErrorInfo | null
}

function ErrorFallback({ error, errorInfo }: ErrorFallbackProps): JSX.Element {
  const handleReset = (): void => {
    window.location.href = '/dashboard'
  }

  const handleReload = (): void => {
    window.location.reload()
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: '#1a1d23',
      }}
    >
      <Card sx={{ maxWidth: 800, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom color="error">
            Something went wrong
          </Typography>
          <Typography variant="body1" paragraph color="text.secondary">
            The page encountered an error and could not be displayed.
          </Typography>

          {error !== null && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'auto',
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Error Details:
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {error.toString()}
              </Typography>

              {errorInfo !== null && (
                <>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 600, mt: 2 }}
                  >
                    Component Stack:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 300,
                      overflow: 'auto',
                    }}
                  >
                    {errorInfo.componentStack}
                  </Typography>
                </>
              )}
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleReset}>
              Return to Dashboard
            </Button>
            <Button variant="outlined" onClick={handleReload}>
              Reload Page
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

// Minimal class component wrapper (required by React for error boundaries)
// eslint-disable-next-line react/require-optimization
class ErrorBoundaryClass extends Component<Props, State> {
  public constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo)
    // eslint-disable-next-line react/no-set-state
    this.setState({ hasError: true, error, errorInfo })
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      )
    }

    return this.props.children
  }
}

// Export the class component as the default (required for error boundaries)
export default ErrorBoundaryClass
